import React, { ChangeEvent, useState } from 'react';
import { BookingImage, BookingImages, Quote } from 'acscp/store';
import { FormatCurrency } from 'acscp/components/common/helpers/formatCurrency';
import style from './quoteDetails.module.scss';
import { CroppedImage } from 'acscp/components/common/croppedImage';
import { defineMessages, FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import classNames from 'classnames';
import { MoreLessText } from 'acscp/components/common/moreLessText';
import { LabeledItem } from 'acscp/components/common/labeledItem';
import { QuoteButtons } from 'acscp/components/quotes/quoteButtons';
import { Department } from 'acscp/api/models/customTypes/department';
import { QuoteType } from 'acscp/api/models/customTypes/quoteType';
import { MoreLessWrapper } from 'acscp/components/common/moreLessWrapper';
import { lengthUnitToString, volumeUnitToHtml } from 'acscp/api/models/customTypes/unitUtils';
import { Duration } from 'acscp/components/common/duration';
import { DurationStyle } from 'acscp/components/common/helpers/formatMoments';
import { LocalTimeWithFallback } from 'acscp/components/common/localTimeWithFallback';
import { TimeType } from 'acscp/api/models/customTypes/timeType';
import MessageDescriptor = ReactIntl.FormattedMessage.MessageDescriptor;
import { isQuoteInAcmiChartersWorkflow, isQuoteInAcmiWorkflow } from 'acscp/store/helpers/quoteHelper';

const messages = defineMessages({
    additionalCosts: {
        id: 'components.quotes.quoteDetails.additionalCosts',
        defaultMessage: 'Additional Costs',
    },
    additionalInformation: {
        id: 'components.quotes.quoteDetails.additionalInformation',
        defaultMessage: 'Additional Information',
    },
    aircraftConfiguration: {
        id: 'components.quotes.quoteDetails.aircraftConfiguration',
        defaultMessage: 'Aircraft Configuration'
    },
    aircraftType: {
        id: 'components.quotes.quoteDetails.aircraftType',
        defaultMessage: 'Aircraft Type'
    },
    airlineName: {
        id: 'components.quotes.quoteDetails.airlineName',
        defaultMessage: 'Airline Name',
    },
    amenities: {
        id: 'components.quotes.quoteDetails.amenities',
        defaultMessage: 'Amenities',
    },
    baggageAllowance: {
        id: 'components.quotes.quoteDetails.baggageAllowance',
        defaultMessage: 'Baggage Allowance',
    },
    billableHours: {
        id: 'components.quotes.quoteDetails.billableHours',
        defaultMessage: 'Billable Hours',
    },
    notes: {
        id: 'components.quotes.quoteDetails.notes',
        defaultMessage: 'Notes',
    },
    priceExcludes: {
        id: 'components.quotes.quoteDetails.priceExcludes',
        defaultMessage: 'Price Excludes',
    },
    priceIncludes: {
        id: 'components.quotes.quoteDetails.priceIncludes',
        defaultMessage: 'Price Includes',
    },
    priceOptions: {
        id: 'components.quotes.quoteDetails.priceOptions',
        defaultMessage: 'Price Options',
    },
    rateExcludes: {
        id: 'components.quotes.quoteDetails.rateExcludes',
        defaultMessage: 'Rate Excludes',
    },
    rateIncludes: {
        id: 'components.quotes.quoteDetails.rateIncludes',
        defaultMessage: 'Rate Includes',
    },
    savingsMade: {
        id: 'components.quotes.quoteDetails.savingsMade',
        defaultMessage: 'Savings Made',
    },
    seatingCapacity: {
        id: 'components.quotes.quoteDetails.seatingCapacity',
        defaultMessage: 'Seating Capacity',
    },
    totalHours: {
        id: 'components.quotes.quoteDetails.totalHours',
        defaultMessage: 'Total Hours'
    },
    termsAndConditions: {
        id: 'components.quotes.quoteDetails.termsAndConditions',
        defaultMessage: 'Terms & Conditions',
    },
    yearOfManufacture: {
        id: 'components.quotes.quoteDetails.yearOfManufacture',
        defaultMessage: 'Year of Manufacture',
    },
    yearOfRefurbishment: {
        id: 'components.quotes.quoteDetails.yearOfRefurbishment',
        defaultMessage: 'Year of Refurbishment',
    },
    cargoDimensions: {
        id: 'components.quotes.quoteDetails.cargoDimensions',
        defaultMessage: 'Cargo Dimensions',
    },
    maximumLoad: {
        id: 'components.quotes.quoteDetails.maximumLoad',
        defaultMessage: 'Maximum Load',
    },
    cargoHold: {
        id: 'components.quotes.quoteDetails.cargoHold',
        defaultMessage: 'Cargo Hold',
    },
    holdVolume: {
        id: 'components.quotes.quoteDetails.holdVolume',
        defaultMessage: 'Hold Volume',
    },
    rearDoorSize: {
        id: 'components.quotes.quoteDetails.rearDoorSize',
        defaultMessage: 'Rear Door Size',
    },
    sideDoorSize: {
        id: 'components.quotes.quoteDetails.sideDoorSize',
        defaultMessage: 'Side Door Size',
    },
    noseDoorSize: {
        id: 'components.quotes.quoteDetails.noseDoorSize',
        defaultMessage: 'Nose Door Size',
    },
    genericDoorSize: {
        id: 'components.quotes.quoteDetails.doorSize',
        defaultMessage: 'Door Size',
    },
    lowerDeckPalletPositions: {
        id: 'components.quotes.quoteDetails.lowerDeckPalletPositions',
        defaultMessage: 'Lower Deck Pallet Positions',
    },
    upperDeckPalletPositions: {
        id: 'components.quotes.quoteDetails.upperDeckPalletPositions',
        defaultMessage: 'Upper Deck Pallet Positions',
    },
    positionTime: {
        id: 'components.quotes.quoteDetails.positionTime',
        defaultMessage: 'Position Time',
    },
    subjectTo: {
        id: 'components.quotes.quoteDetails.subjectTo',
        defaultMessage: 'Subject To',
    },
    excludes: {
        id: 'components.quotes.quoteDetails.excludes',
        defaultMessage: 'Excludes',
    },
    paymentTerms: {
        id: 'components.quotes.quoteDetails.paymentTerms',
        defaultMessage: 'Payment Terms',
    },
    charterPrice: {
        id: 'components.quotes.quoteDetails.charterPrice',
        defaultMessage: 'Charter Price',
    },
    registration: {
        id: 'components.quotes.quoteDetails.registration',
        defaultMessage: 'Registration',
    },
    crews: {
        id: 'components.quotes.quoteDetails.crews',
        defaultMessage: 'Crews',
    },
    acmiRate: {
        id: 'components.quotes.quoteDetails.acmiRate',
        defaultMessage: 'ACMI Rate',
    },
    crewPerDiem: {
        id: 'components.quotes.quoteDetails.crewPerDiem',
        defaultMessage: 'Crew per Diem',
    },
    excessHours: {
        id: 'components.quotes.quoteDetails.excessHours',
        defaultMessage: 'Excess Hours',
    },
});

const QuoteSeatingCapacity: React.FC<{
    aircraftSeatingCapacityString: string,
    isQuoteDetailsTab: boolean,
} & InjectedIntlProps> = ({
    aircraftSeatingCapacityString,
    isQuoteDetailsTab,
    intl,
                              }) => (
    <LabeledItem
        label={
            intl.formatMessage(messages.seatingCapacity)
        }
        className={classNames(style.InfoItem, {[style.PropertyBelowLabel]: isQuoteDetailsTab})}
    >
        <FormattedMessage
            id={'components.quotes.quoteDetails.seatingCapacityValue'}
            defaultMessage={'Up to {aircraftSeatingCapacity} passengers'}
            values={{aircraftSeatingCapacity: aircraftSeatingCapacityString}}
        />
    </LabeledItem>
);

const QuoteSeatingCapacityWithIntl = injectIntl(QuoteSeatingCapacity);

const QuoteDetailsLabeledItem: React.FC<{
    translation: MessageDescriptor,
    isQuoteDetailsTab: boolean,
    text: JSX.Element | string | null,
    linebreak?: boolean,
} & InjectedIntlProps> = ({translation, isQuoteDetailsTab, text, linebreak, intl}) => (
    <LabeledItem
        label={
            intl.formatMessage(translation)
        }
        className={classNames(style.InfoItem, {[style.PropertyBelowLabel]: isQuoteDetailsTab})}
        linebreak={linebreak}
    >
        {text}
    </LabeledItem>
);

const QuoteDetailsLabeledHtml: React.FC<{
    translation: MessageDescriptor,
    isQuoteDetailsTab: boolean,
    htmlString: string,
    linebreak?: boolean,
} & InjectedIntlProps> = ({translation, isQuoteDetailsTab, htmlString, linebreak, intl}) => (
    <LabeledItem
        label={
            intl.formatMessage(translation)
        }
        className={classNames(style.InfoItem, {[style.PropertyBelowLabel]: isQuoteDetailsTab})}
        linebreak={linebreak}
    >
        <div dangerouslySetInnerHTML={{__html: htmlString}}/>
    </LabeledItem>
);

const QuoteDetailsLabeledItemWithUnit: React.FC<{
    translation: MessageDescriptor,
    isQuoteDetailsTab: boolean,
    text: string | null,
    unitHtmlOrString: JSX.Element | string,
    linebreak?: boolean,
} & InjectedIntlProps> = ({translation, isQuoteDetailsTab, text, linebreak, intl, unitHtmlOrString}) => (
    <LabeledItem
        label={
            intl.formatMessage(translation)
        }
        className={classNames(style.InfoItem, {[style.PropertyBelowLabel]: isQuoteDetailsTab})}
        linebreak={linebreak}
    >
        <span>{text} {unitHtmlOrString}</span>
    </LabeledItem>
);

const QuoteDetailsLabeledItemWithIntl = injectIntl(QuoteDetailsLabeledItem);
const QuoteDetailsLabeledItemWithUnitWithIntl = injectIntl(QuoteDetailsLabeledItemWithUnit);
const QuoteDetailsLabeledHtmlWithIntl = injectIntl(QuoteDetailsLabeledHtml);

const QuoteDetailsGoNowContentItem: React.FC<{
    translation: MessageDescriptor,
    htmlContent: string | null,
} & InjectedIntlProps> = ({translation, htmlContent, intl}) => (
    <LabeledItem
        label={
            intl.formatMessage(translation)
        }
        className={classNames(style.PropertyBelowLabel)}
    >
        {htmlContent &&
            <div dangerouslySetInnerHTML={{__html: htmlContent}}/>
        }
    </LabeledItem>
);
const QuoteDetailsGoNowContentItemWithIntl = injectIntl(QuoteDetailsGoNowContentItem);

const QuoteDetailsQuotingNote: React.FC<{
    note: string | null,
    htmlNote: string | null,
    translation: MessageDescriptor,
 } & InjectedIntlProps> = ({note, htmlNote, translation, intl}) => (
    <>
        {htmlNote &&
            <div className={style.NotesSectionBrokerNotes}>
                <LabeledItem label={intl.formatMessage(translation)}>
                    <MoreLessText unsafeHTML={htmlNote} minimumLines={2}/>
                </LabeledItem>
            </div>
        }
        {note && !htmlNote &&
            <div className={style.NotesSectionBrokerNotes}>
                <p className={style.NotesSectionHeader}>
                    {intl.formatMessage(translation)}
                </p>
                <MoreLessText text={note} minimumLines={2}/>
            </div>
        }
    </>
);
const QuoteDetailsQuotingNoteWithIntl = injectIntl(QuoteDetailsQuotingNote);

const QuoteDetailsNotesList: React.FC<{
    list: Array<{name: string|null, notes: string|null}>,
    translation: MessageDescriptor,
    isExpandedByDefault: boolean,
} & InjectedIntlProps> = ({list, translation, isExpandedByDefault, intl}) => {
   
    const nameClass = classNames(style.NotesSectionHeader);
    const [expanded, setExpanded] = useState(isExpandedByDefault);

    if (list.length === 0) {
        return null;
    }

    return (
        <div className={style.NotesSectionBrokerNotes}>
            <p className={style.NotesSectionHeader}>
                {intl.formatMessage(translation)}:
            </p>
            <MoreLessWrapper handleChange={setExpanded} expanded={expanded} shouldRenderMoreLessButton={list.length > 2}>
            <div className={style.PriceModificationsContainer}>
                <ul>
                    {list.slice(0, expanded ? list.length : 2).map((pm, i) => (
                        <li key={pm.name || i.toString()}>
                            {pm.name && pm.notes
                                ? <span className={nameClass}>{pm.name}: </span>
                                : pm.name}
                            {pm.notes}
                        </li>
                    ))}
                </ul>
            </div>
            </MoreLessWrapper>
        </div>
    );
};
const QuoteDetailsNotesListWithIntl = injectIntl(QuoteDetailsNotesList);

const getGoNowPositionTime = (quote: Quote) => {
    if (quote.goNowPositionTime !== null) {
        const timeTypeString = quote.goNowPositionTimeType === TimeType.UTC
            ? (
                <FormattedMessage
                    id={'components.quotes.quoteDetails.goNowPositionTimeUTC'}
                    defaultMessage={'UTC'}
                />
            )
            : (
                <FormattedMessage
                    id={'components.quotes.quoteDetails.goNowPositionTimeLocal'}
                    defaultMessage={'Local'}
                />
            );
        return (
            <span>
                <LocalTimeWithFallback dateTime={quote.goNowPositionTime}/>
                {' '}
                {timeTypeString}
            </span>
        );
    }

    if (quote.goNowPositionDuration !== null) {
        return <Duration duration={quote.goNowPositionDuration} style={DurationStyle.Minimal}/>;
    }

    return null;
};

const QuoteTextSections: React.FC<{
    quote: Quote,
    isQuoteDetailsTab: boolean,
    jetCardHolder: boolean,
    jetCardType: string | null,
    userNotesInputChangeHandler?: (event: ChangeEvent<HTMLTextAreaElement>) => void,
} & InjectedIntlProps> = ({ quote, isQuoteDetailsTab, userNotesInputChangeHandler, jetCardHolder, jetCardType, intl}) => {
    const shouldShowGenericDoorSizeLabel = [
        quote.aircraftCargoInformation.sideDoor,
        quote.aircraftCargoInformation.noseDoor,
        quote.aircraftCargoInformation.rearDoor,
    ].filter(doorSize => !!doorSize).length === 1;

    const isInAcmiWorkflow = isQuoteInAcmiWorkflow(quote);
    const isInAcmiCharterWorkflow = isQuoteInAcmiChartersWorkflow(quote);
    return (
        <div className={classNames(style.TextSections, {[style.NotesBelow]: isQuoteDetailsTab})}>
            {quote.quotingNotes.htmlQuoteIntroduction &&
                <div className={classNames(style.NotesSection, {[style.InfoAsGrid]: isQuoteDetailsTab})}>
                    <div dangerouslySetInnerHTML={{__html: quote.quotingNotes.htmlQuoteIntroduction}} />
                </div>
            }
            {quote.quotingNotes.quoteIntroduction && !quote.quotingNotes.htmlQuoteIntroduction &&
                <div className={classNames(style.NotesSection, {[style.InfoAsGrid]: isQuoteDetailsTab})}>
                    <span>{quote.quotingNotes.quoteIntroduction}</span>
                </div>
            }
            <div className={classNames(style.InfoSection, {[style.InfoAsGrid]: isQuoteDetailsTab})}>
                {!isQuoteDetailsTab && <LabeledItem
                    label={
                        <FormattedMessage
                            id={'components.quotes.quoteDetails.operator'}
                            defaultMessage={'Operator'}
                        />
                    }
                    className={classNames(style.InfoItem)}
                >
                    {quote.airline}
                </LabeledItem>}
                <QuoteDetailsLabeledItemWithIntl
                    translation={messages.aircraftType}
                    isQuoteDetailsTab={isQuoteDetailsTab}
                    text={quote.aircraftType}
                />
                {quote.department === Department.Cargo &&
                    <>
                        {quote.cargoDimensions &&
                            <QuoteDetailsLabeledHtmlWithIntl
                                translation={messages.cargoDimensions}
                                htmlString={quote.cargoDimensions}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                            />
                        }
                        {quote.quoteType === QuoteType.GoNow &&
                            <QuoteDetailsLabeledItemWithIntl
                                translation={messages.positionTime}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={getGoNowPositionTime(quote)}
                            />
                        }
                        {quote.aircraftCargoInformation.cargoHold &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={messages.cargoHold}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.cargoHold}
                                unitHtmlOrString={lengthUnitToString(quote.aircraftCargoInformation.cargoHoldUnit)}
                            />
                        }
                        {quote.aircraftCargoInformation.holdVolume &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={messages.holdVolume}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.holdVolume.toString()}
                                unitHtmlOrString={volumeUnitToHtml(quote.aircraftCargoInformation.holdVolumeUnit)}
                            />
                        }
                        {quote.aircraftCargoInformation.maximumPayload &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={messages.maximumLoad}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.maximumPayload.toString()}
                                unitHtmlOrString={quote.aircraftCargoInformation.maximumPayloadUnit
                                    ? quote.aircraftCargoInformation.maximumPayloadUnit.toString()
                                    : ''
                                }
                            />
                        }
                        {quote.aircraftCargoInformation.sideDoor &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={shouldShowGenericDoorSizeLabel
                                    ? messages.genericDoorSize
                                    : messages.sideDoorSize
                                }
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.sideDoor}
                                unitHtmlOrString={lengthUnitToString(quote.aircraftCargoInformation.sideDoorUnit)}
                            />
                        }
                        {quote.aircraftCargoInformation.rearDoor &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={shouldShowGenericDoorSizeLabel
                                    ? messages.genericDoorSize
                                    : messages.rearDoorSize}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.rearDoor}
                                unitHtmlOrString={lengthUnitToString(quote.aircraftCargoInformation.rearDoorUnit)}
                            />
                        }
                        {quote.aircraftCargoInformation.noseDoor &&
                            <QuoteDetailsLabeledItemWithUnitWithIntl
                                translation={shouldShowGenericDoorSizeLabel
                                    ? messages.genericDoorSize
                                    : messages.noseDoorSize
                                }
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.noseDoor}
                                unitHtmlOrString={lengthUnitToString(quote.aircraftCargoInformation.noseDoorUnit)}
                            />
                        }
                        {quote.aircraftCargoInformation.lowerDeckPalletPositions &&
                            <QuoteDetailsLabeledItemWithIntl
                                translation={messages.lowerDeckPalletPositions}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.lowerDeckPalletPositions}
                            />
                        }
                        {quote.aircraftCargoInformation.upperDeckPalletPositions &&
                            <QuoteDetailsLabeledItemWithIntl
                                translation={messages.upperDeckPalletPositions}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.aircraftCargoInformation.upperDeckPalletPositions}
                            />
                        }
                        <div className={style.CargoDisclaimer}>
                            <FormattedMessage
                                id={'components.quotes.quoteDetails.cargoDisclaimer'}
                                defaultMessage={'*Cargo loadability is subject to final confirmation from airline'}
                            />
                        </div>
                    </>
                }
                {(quote.department === Department.COMMJ || quote.department === Department.ACMI)
                    && quote.seatingConfigurationNotes &&
                    <QuoteDetailsLabeledItemWithIntl
                        translation={messages.aircraftConfiguration}
                        isQuoteDetailsTab={isQuoteDetailsTab}
                        text={quote.seatingConfigurationNotes}
                    />
                }
                {quote.airline &&
                    <QuoteDetailsLabeledItemWithIntl
                        translation={messages.airlineName}
                        isQuoteDetailsTab={isQuoteDetailsTab}
                        text={
                            quote.airline + ((quote.alternativeAirlineNames && quote.alternativeAirlineNames.length > 0)
                                ? ` (${quote.alternativeAirlineNames.join(', ')})`
                                : '')
                        }
                    />
                }
                {quote.department === Department.PJETS &&
                    <>
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.yearOfManufacture}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.aircraftYearOfManufacture}
                        />
                        {quote.aircraftSeatingCapacityString
                            ?
                            <QuoteSeatingCapacityWithIntl
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                aircraftSeatingCapacityString={quote.aircraftSeatingCapacityString}
                            />
                            : null
                        }
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.yearOfRefurbishment}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.aircraftYearOfRefurbishment}
                        />
                    </>
                }
                {(quote.department === Department.COMMJ || isInAcmiCharterWorkflow) &&
                    <QuoteDetailsLabeledItemWithIntl
                        translation={messages.baggageAllowance}
                        isQuoteDetailsTab={isQuoteDetailsTab}
                        text={quote.baggageAllowanceNotes}
                    />
                }
                {isInAcmiWorkflow &&
                    <>
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.registration}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.registration}
                        />
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.acmiRate}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={`${quote.acmiRateCurrency ? `${quote.acmiRateCurrency} ` : ''}${quote.acmiRate} per block hour`}
                        />
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.excessHours}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={`${quote.excessHoursCurrency ? `${quote.excessHoursCurrency} ` : ''}${quote.excessHours} per block hour`}
                        />
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.crewPerDiem}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.isCrewPerDiemIncluded === null ? null
                                    : quote.isCrewPerDiemIncluded ? 'Included' : 'Excluded'}
                        />
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.crews}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.crews}
                        />
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.yearOfManufacture}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.aircraftYearOfManufacture}
                        />
                    </>
                }

                {jetCardHolder
                    ?
                    <>
                        {quote.jetCardRate &&
                            <LabeledItem
                                label={jetCardType!}
                                className={classNames(style.InfoItem, {[style.PropertyBelowLabel]: isQuoteDetailsTab})}
                            >
                                <FormatCurrency currency={quote.jetCardRateCurrency} price={quote.jetCardRate} />
                            </LabeledItem>}
                        {quote.totalHours &&
                            <QuoteDetailsLabeledItemWithIntl
                                translation={messages.totalHours}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.totalHours.toString()}
                            />
                        }
                        {quote.billableHours &&
                            <QuoteDetailsLabeledItemWithIntl
                                translation={messages.billableHours}
                                isQuoteDetailsTab={isQuoteDetailsTab}
                                text={quote.billableHours.toString()}
                            />
                        }
                        <QuoteDetailsLabeledItemWithIntl
                            translation={messages.savingsMade}
                            isQuoteDetailsTab={isQuoteDetailsTab}
                            text={quote.savingsMade}
                        />
                    </>
                    : quote.department !== Department.Cargo && !isInAcmiWorkflow
                    && quote.sellPrice !== null &&
                    <QuoteDetailsLabeledItemWithIntl
                        translation={messages.additionalCosts}
                        isQuoteDetailsTab={isQuoteDetailsTab}
                        text={quote.additionalCosts}
                    />
                }
                {!isQuoteDetailsTab && <LabeledItem
                    label={
                        <FormattedMessage
                            id={'components.quotes.quoteDetails.cancellationTerms'}
                            defaultMessage={'Cancellation Terms'}
                        />
                    }
                    className={style.CancellationTerms}
                >
                    {quote.cancellationTerms && <span className={style.CancellationTermsText}>{quote.cancellationTerms}</span>}
                </LabeledItem>}
            </div>
            <div className={classNames(style.NotesSection, {[style.InfoAsGrid]: isQuoteDetailsTab})}>
                {quote.department !== Department.PJETS &&
                    (isInAcmiWorkflow ? (
                        <>
                            {quote.isRateIncludedDisplayedOnQuote &&
                                <QuoteDetailsNotesListWithIntl
                                    list={quote.priceInclusions}
                                    translation={messages.rateIncludes}
                                    isExpandedByDefault={true}
                                />
                            }
                            {quote.isRateExcludedDisplayedOnQuote &&
                                <QuoteDetailsNotesListWithIntl
                                    list={quote.priceExclusions}
                                    translation={messages.rateExcludes}
                                    isExpandedByDefault={true}
                                />
                            }
                        </>
                    ) : (
                        <>
                            <QuoteDetailsNotesListWithIntl
                                list={quote.priceInclusions}
                                translation={messages.priceIncludes}
                                isExpandedByDefault={true}
                            />
                            <QuoteDetailsNotesListWithIntl
                                list={quote.priceExclusions}
                                translation={messages.priceExcludes}
                                isExpandedByDefault={true}
                            />
                        </>
                    ))
                }
                {(quote.department === Department.COMMJ || isInAcmiCharterWorkflow) &&
                    <>
                        {quote.quotingNotes.additionalInformation &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={quote.quotingNotes.additionalInformation}
                                htmlNote={quote.quotingNotes.htmlAdditionalInformation}
                                translation={messages.additionalInformation}
                            />
                        }
                        {quote.quotingNotes.termsAndConditions &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={quote.quotingNotes.termsAndConditions}
                                htmlNote={quote.quotingNotes.htmlTermsAndConditions}
                                translation={messages.termsAndConditions}
                            />
                        }
                    </>
                }
                {isInAcmiWorkflow &&
                    <>
                        {quote.quotingNotes.additionalInformation &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={quote.quotingNotes.additionalInformation}
                                htmlNote={quote.quotingNotes.htmlAdditionalInformation}
                                translation={messages.additionalInformation}
                            />
                        }
                        {quote.additionalCosts &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={quote.additionalCosts}
                                htmlNote={quote.additionalCosts}
                                translation={messages.additionalCosts}
                            />
                        }
                    </>
                }
                {/* This notes is PJETS only so the label won't clash with cargo customer notes*/}
                <QuoteDetailsQuotingNoteWithIntl
                    note={quote.quotingNotes.notes}
                    htmlNote={quote.quotingNotes.htmlNotes}
                    translation={messages.notes}
                />
                <QuoteDetailsNotesListWithIntl
                    list={quote.amenities}
                    translation={messages.amenities}
                    isExpandedByDefault={false}
                />
                {quote.department === Department.Cargo && quote.quotingNotes.customerNotes &&
                    <QuoteDetailsQuotingNoteWithIntl
                        note={quote.quotingNotes.customerNotes}
                        htmlNote={quote.quotingNotes.htmlCustomerNotes}
                        translation={messages.notes}
                    />
                }
                {quote.department === Department.Cargo &&
                    <div className={style.MobileOnly}>
                        {quote.quotingNotes.htmlQuoteSubjectTo &&
                            <QuoteDetailsQuotingNoteWithIntl
                                translation={messages.subjectTo}
                                htmlNote={quote.quotingNotes.htmlQuoteSubjectTo}
                                note={null}
                            />
                        }
                        {quote.quoteType !== QuoteType.GoNow && quote.quotingNotes.htmlPaymentTerms &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={null}
                                htmlNote={quote.quotingNotes.htmlPaymentTerms}
                                translation={messages.paymentTerms}
                            />
                        }
                        {quote.quoteType === QuoteType.GoNow && quote.quotingNotes.htmlGoNowExcludes &&
                            <QuoteDetailsQuotingNoteWithIntl
                                note={null}
                                htmlNote={quote.quotingNotes.htmlGoNowExcludes}
                                translation={messages.excludes}
                            />
                        }
                    </div>
                }
                {userNotesInputChangeHandler ? (
                    <div>
                        <p className={style.NotesSectionHeader}>
                            {quote.userAcceptanceNotes ? (
                                <FormattedMessage
                                    id={'components.quotes.quoteDetails.updateYourNotes'}
                                    defaultMessage={'Update your notes (optional)'}
                                />
                            ) : (
                                <FormattedMessage
                                    id={'components.quotes.quoteDetails.yourNotesOptional'}
                                    defaultMessage={'Your notes (optional)'}
                                />
                            )}
                        </p>
                        {jetCardHolder
                            ? <FormattedMessage
                                id={'components.quotes.quoteDetails.jetCardUserNotesLabel'}
                                defaultMessage={'These will be sent to your account manager when you '
                                    + 'confirm and book this quote'}
                            >
                                {(msg: string) => (
                                    <textarea
                                        className={style.UserNotes}
                                        onChange={userNotesInputChangeHandler}
                                        placeholder={msg}
                                        maxLength={300}
                                        defaultValue={quote.userAcceptanceNotes || ''}
                                    />
                                )}
                            </FormattedMessage>
                            : <FormattedMessage
                                id={'components.quotes.quoteDetails.userNotesLabel'}
                                defaultMessage={'These will be sent to your account manager when you '
                                    + 'confirm and accept this quote'}
                            >
                                {(msg: string) => (
                                    <textarea
                                        className={style.UserNotes}
                                        onChange={userNotesInputChangeHandler}
                                        placeholder={msg}
                                        maxLength={300}
                                        defaultValue={quote.userAcceptanceNotes || ''}
                                    />
                                )}
                            </FormattedMessage>
                        }
                    </div>
                ) : quote.userAcceptanceNotes &&
                    <div>
                        <p className={style.NotesSectionHeader}>
                            <FormattedMessage
                                id={'components.quotes.quoteDetails.yourNotes'}
                                defaultMessage={'Your notes'}
                            />
                        </p>
                        <MoreLessText text={quote.userAcceptanceNotes} />
                    </div>
                }
                {jetCardHolder && <>
                    {quote.jetCardStandardCancellationTermsText && <div className={style.NotesSectionBrokerNotes}>
                        <p className={style.NotesSectionHeader}>
                            <FormattedMessage
                                id={'components.quotes.quoteDetails.jetCardCancellationTermsSectionTitle'}
                                defaultMessage={'CANCELLATION TERMS & FEES'}
                            />
                        </p>
                        <MoreLessText
                            unsafeHTML={`
                            ${intl.formatMessage({
                                id: 'components.quotes.quoteDetails.jetCardStandardCancellationTermsTitle',
                                defaultMessage: 'Standard ACS Terms'
                            })}:<br/>
                            ${quote.jetCardStandardCancellationTermsText}<br/>
                            <br/>
                            ${intl.formatMessage({
                                id: 'components.quotes.quoteDetails.jetCardCancellationTermsTitle',
                                defaultMessage: 'Cancellation Terms'
                            })}:<br/>
                            ${
                                [
                                    quote.jetCardCancellationTermsImmediateText,
                                    quote.jetCardCancellationTermsThreshold1Text,
                                    quote.jetCardCancellationTermsThreshold2Text,
                                    quote.jetCardCancellationTermsThreshold3Text
                                ].filter(s => !!s).join('<br/>')
                            }
                        `}
                            minimumLines={2}
                        />
                    </div>}
                    {quote.additionalTerms && <div className={style.NotesSectionBrokerNotes}>
                        <p className={style.NotesSectionHeader}>
                            <FormattedMessage
                                id={'components.quotes.quoteDetails.additionalTerms'}
                                defaultMessage={'Additional Ts & Cs'}
                            />
                        </p>
                        <MoreLessText unsafeHTML={quote.additionalTerms!} minimumLines={2}/>
                    </div>}
                </>}
                {quote.isAmex &&
                    <div className={style.NotesSectionHeader}>
                        <div dangerouslySetInnerHTML={{__html: quote.amexNotes!}} />
                    </div>
                }
            </div>
        </div>
    );
};

const CharterPrice: React.FC<{
    quote: Quote,
    jetCardHolder: boolean,
    className?: string,
    labelClassName?: string,
    currencyClassName?: string,
    priceClassName?: string,
    amexClassName?: string,
    price?: number | null,
}> = ({
    quote,
    jetCardHolder,
    className,
    labelClassName,
    currencyClassName,
    priceClassName,
    amexClassName,
    price
 }) => {
    return (
        <LabeledItem
            label={
                jetCardHolder
                    ? <FormattedMessage
                        id={'components.quotes.quoteDetails.currentMarketPrice'}
                        defaultMessage={'Current Market Price'}
                    />
                    : quote.quoteType === QuoteType.Series
                        ?
                        <FormattedMessage
                            id={'components.quotes.quoteDetails.totalCharterPrice'}
                            defaultMessage={'Total Charter Price'}
                        />
                    :
                        <FormattedMessage
                            id={'components.quotes.quoteDetails.charterPrice'}
                            defaultMessage={'Charter Price'}
                        />
            }
            labelClassName={labelClassName}
            className={className}
            includeColon={quote.department === Department.Cargo}
        >
            <div className={quote.sellType && style.SellTypeContainer}>
                <div className={currencyClassName}>
                    <FormatCurrency
                        currency={quote.sellCurrency}
                        price={price || quote.sellPrice}
                        numberClass={priceClassName}
                    />
                    {quote.department === Department.Cargo && quote.additionalCosts &&
                        <div className={style.AdditionalCostsNearCharterPrice}>
                            {quote.additionalCosts}
                        </div>
                    }
                    {quote.isAmex &&
                        <div className={amexClassName}>
                            {quote.amexPriceNote}
                        </div>
                    }
                    {quote.department === Department.Cargo && quote.priceOptions &&
                        <QuoteDetailsQuotingNoteWithIntl
                            note={null}
                            htmlNote={quote.priceOptions}
                            translation={messages.priceOptions}
                        />
                    }
                </div>
                {quote.sellType &&
                    <div className={style.SellType}>
                        {quote.sellType}
                    </div>
                }
            </div>
        </LabeledItem>
    );
};

const QuoteImagesSection: React.FC<{
    quote: Quote,
    userIsLoggedIn: boolean,
    jetCardHolder: boolean,
    onShowImageGallery: () => void,
    onShowFloorplan: () => void,
    disabled?: boolean,
}> = ({
    quote,
    userIsLoggedIn,
    jetCardHolder,
    onShowImageGallery,
    onShowFloorplan,
    disabled,
}) => {
        const showImages = quote.images && quote.images.all.length > 0;
        const showAcceptButton = ENABLE_CREATE_ACCOUNT || (userIsLoggedIn && quote.department === Department.PJETS);

        const isInAcmiWorkflow = isQuoteInAcmiWorkflow(quote);
        return (
            <div className={style.ImageSection}>
                {showImages && <QuoteImages
                    images={quote.images}
                    onShowImageGallery={onShowImageGallery}
                    onShowFloorplan={onShowFloorplan}
                    greyedOut={disabled}
                    includeDisclaimer={quote.shouldShowPictureTypicalExampleText}
                />}
                {quote.quoteType === QuoteType.GoNow &&
                    <div className={style.GoNowSection}>
                        {quote.quotingNotes.htmlQuoteSubjectTo &&
                            <div className={style.InfoItem}>
                                <QuoteDetailsGoNowContentItemWithIntl
                                    translation={messages.subjectTo}
                                    htmlContent={quote.quotingNotes.htmlQuoteSubjectTo}
                                />
                            </div>
                        }
                        {quote.quotingNotes.htmlGoNowExcludes &&
                            <div className={style.MarginTop}>
                                <QuoteDetailsGoNowContentItemWithIntl
                                    translation={messages.excludes}
                                    htmlContent={quote.quotingNotes.htmlGoNowExcludes}
                                />
                            </div>
                        }
                    </div>
                }
                {quote.department === Department.Cargo && quote.quoteType !== QuoteType.GoNow &&
                    (quote.quotingNotes.htmlQuoteSubjectTo || quote.quotingNotes.htmlPaymentTerms) &&
                    <div className={style.GoNowSection}>
                        {quote.quotingNotes.htmlQuoteSubjectTo &&
                            <div className={style.InfoItem}>
                                <QuoteDetailsGoNowContentItemWithIntl
                                    translation={messages.subjectTo}
                                    htmlContent={quote.quotingNotes.htmlQuoteSubjectTo}
                                />
                            </div>
                        }
                        {quote.quotingNotes.htmlPaymentTerms &&
                            <div className={style.MarginTop}>
                                <QuoteDetailsGoNowContentItemWithIntl
                                    translation={messages.paymentTerms}
                                    htmlContent={quote.quotingNotes.htmlPaymentTerms}
                                />
                            </div>
                        }
                    </div>
                }

                {/*showAcceptButton is already PJETS so no need to check for Cargo or GoNow*/}
                {showAcceptButton && quote.sellPrice !== null && !isInAcmiWorkflow &&
                    <CharterPrice
                        quote={quote}
                        jetCardHolder={jetCardHolder}
                        className={style.CharterPrice}
                        labelClassName={style.CharterPriceLabel}
                        priceClassName={style.CharterPriceCurrency}
                        amexClassName={style.AmexNote}
                    />
                }
                <QuoteButtons
                    className={classNames(
                        style.ButtonGroup,
                        !showAcceptButton && style.MarginTop,
                        isInAcmiWorkflow && style.JustifyContentEnd,
                    )}
                    quote={quote}
                    jetCardHolder={jetCardHolder}
                    showAcceptButton={showAcceptButton}
                    disabled={disabled}
                />
            </div>
        );
};

const QuoteThumbnail: React.SFC<{ image?: BookingImage, onClick: () => void, greyedOut?: boolean}>
    = ({ image, onClick, greyedOut }) => {
    return (
        image ? (
            <div className={classNames(style.Image, {[style.GreyedOut]: greyedOut})} onClick={onClick}>
                <CroppedImage
                    className={style.quoteThumbnail}
                    ratio={16 / 9}
                    src={image.thumbnailUrl}
                />
            </div>
        ) : null
    );
};

const Disclaimer: React.SFC<{}> = () => {
    return (
        <FormattedMessage
            id={'components.quotes.quoteDetails.disclaimer'}
            defaultMessage={'Pictures are typical examples only.'}
        />
    );
};

const QuoteImages: React.SFC<{
    images: BookingImages,
    onShowImageGallery: () => void,
    onShowFloorplan: () => void,
    greyedOut?: boolean,
    includeDisclaimer: boolean,
}>
    = ({ images, onShowImageGallery, onShowFloorplan, greyedOut, includeDisclaimer }) => {
    const numberOfImages = (images.exterior ? 1 : 0) + (images.interior ? 1 : 0);
    const numberOfFloorplanImages = images.floorplan ? 1 : 0;
    return (
        <div className={style.QuoteImages}>
            <QuoteThumbnail image={images.exterior} onClick={onShowImageGallery} greyedOut={greyedOut} />
            <QuoteThumbnail image={images.interior} onClick={onShowImageGallery} greyedOut={greyedOut} />
            <div className={style.ImageBar}>
                <span className={style.ImageGalleryLink} onClick={onShowImageGallery}>
                    <FormattedMessage
                        id={'components.quotes.quoteDetails.imageBar.photos'}
                        defaultMessage={'PHOTOS ({numberOfImages}) '}
                        values={{ numberOfImages }}
                    />
                </span>
                <span className={style.ImageGalleryLink} onClick={onShowFloorplan}>
                    <FormattedMessage
                        id={'components.quotes.quoteDetails.imageBar.floorplan'}
                        defaultMessage={'FLOORPLAN ({numberOfFloorplanImages})'}
                        values={{ numberOfFloorplanImages }}
                    />
                </span>
            </div>
            {includeDisclaimer && <div className={style.Disclaimer}>
                <Disclaimer/>
            </div>}
        </div>
    );
};
const QuoteTextSectionsWithIntl = injectIntl(QuoteTextSections);

interface QuoteDetailsProps {
    quote: Quote;
    userIsLoggedIn: boolean;
    jetCardHolder: boolean;
    jetCardType: string | null;
    onShowImageGallery: () => void;
    onShowFloorplan: () => void;
    disabled?: boolean;
}

const QuoteDetails: React.FC<QuoteDetailsProps> =
    ({onShowImageGallery, onShowFloorplan, quote, userIsLoggedIn, jetCardHolder, jetCardType, disabled}) => {
    return (
        <div className={style.QuoteDetails}>
            <QuoteImagesSection
                quote={quote}
                userIsLoggedIn={userIsLoggedIn}
                onShowImageGallery={onShowImageGallery}
                onShowFloorplan={onShowFloorplan}
                jetCardHolder={jetCardHolder}
                disabled={disabled}
            />
            <QuoteTextSectionsWithIntl
                quote={quote}
                isQuoteDetailsTab={true}
                jetCardHolder={jetCardHolder}
                jetCardType={jetCardType}
            />
        </div>
    );
};

export { QuoteTextSectionsWithIntl as QuoteTextSections, QuoteDetails, CharterPrice, Disclaimer };
